import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getApiConfiguration } from '../../api';
import { TeamsApi } from '../../api-client';
import { useUserStore } from './userStore';
import { useRouter } from 'vue-router';

const teamsClient = new TeamsApi(getApiConfiguration(SERVER_URL_BASE));

export const useTeamStore = defineStore('teamStore', () => {
    const router = useRouter();
    const userStore = useUserStore();
    const currentTeam = ref(null);
    const subscriptionStatus = ref(null);
    const paymentMethods = ref([]);
    const isLoading = ref(false);
    const error = ref(null);

    const isTeamLoaded = computed(() => !!currentTeam.value);
    const isAuthenticated = computed(() => userStore.isAuthenticated);

    const defaultPaymentMethod = computed(() => {
        return paymentMethods.value?.find(method => method.isDefault) || paymentMethods.value?.[0]
    })

    function isCardExpiringThisYear(card) {
        if (!card) {
            return false
        }

        const currentDate = new Date()
        const currentYear = currentDate.getFullYear()
        const currentMonth = currentDate.getMonth() + 1 // JavaScript months are 0-based

        const cardExpYear = card.exp_year
        const cardExpMonth = card.exp_month

        // Check if card is expired
        if (cardExpYear < currentYear ||
            (cardExpYear === currentYear && cardExpMonth < currentMonth)) {
            return true
        }

        // Check if card expires this year
        return cardExpYear === currentYear
    }

    const isDefaultPaymentMethodExpiringThisYear = computed(() => {
        const defaultMethod = defaultPaymentMethod.value
        return defaultMethod?.card ? isCardExpiringThisYear(defaultMethod.card) : false
    })

    async function ensureUserLoaded() {
        if (!userStore.user) {
            await userStore.fetchUserDetails();
        }
        return isAuthenticated.value;
    }

    async function fetchTeamFromSource(teamSlug, returnTeam = false) {
        const isAuthed = await ensureUserLoaded();
        if (!isAuthed) {
            error.value = 'User must be authenticated to fetch team details';
            return null;
        }

        isLoading.value = true;
        error.value = null;
        try {
            // First try to get team from user's teams
            if (userStore.user?.teams) {
                const userTeam = userStore.getTeamBySlug(teamSlug);
                if (userTeam) {
                    currentTeam.value = userTeam;
                    subscriptionStatus.value = userTeam.subscription?.status || 'inactive';
                    return returnTeam ? userTeam : undefined;
                }
            }

            // If not found in user's teams, fetch from API
            const team = await teamsClient.teamsGetBySlug({ teamSlug });

            if (team) {
                currentTeam.value = team;
                subscriptionStatus.value = team.subscription?.status || 'inactive';
                return returnTeam ? team : undefined;
            } else {
                console.error(`Team not found for slug: ${teamSlug}`);
                currentTeam.value = null;
                subscriptionStatus.value = null;
                error.value = 'Team not found';
                return null;
            }
        } catch (err) {
            console.error(`Failed to fetch team${returnTeam ? ' by slug' : ' details'}: ${teamSlug}`, err);
            currentTeam.value = null;
            subscriptionStatus.value = null;
            error.value = err.message;
            return null;
        } finally {
            isLoading.value = false;
        }
    }

    async function fetchTeamDetails(teamSlug) {
        await fetchTeamFromSource(teamSlug, false);
    }

    async function getTeamBySlug(teamSlug) {
        return await fetchTeamFromSource(teamSlug, true);
    }

    async function getTeamPaymentMethods(teamSlug) {
        const isAuthed = await ensureUserLoaded();
        if (!isAuthed) {
            error.value = 'User must be authenticated to fetch payment methods';
            return;
        }

        isLoading.value = true;
        error.value = null;
        try {
            const team = await getTeamBySlug(teamSlug);
            paymentMethods.value = team?.paymentMethods || [];
        } catch (err) {
            console.error('Failed to fetch team payment methods', err);
            paymentMethods.value = [];
            error.value = err.message;
        } finally {
            isLoading.value = false;
        }
    }

    async function navigateToAnnualReportCart(teamSlug) {
        try {
            const team = await getTeamBySlug(teamSlug);
            if (team?.companyUuid) {
                // Navigate to the first step with company UUID
                router.push({
                    name: 'Portal_CartStep',
                    params: {
                        cartSlug: 'florida-annual-report',
                        teamSlug: teamSlug,
                        stepIndex: '0'
                    },
                    query: { company_uuid: team.companyUuid }
                });
            } else {
                // Navigate to cart landing page if no company UUID
                router.push({
                    name: 'Portal_CartLandingPage',
                    params: {
                        cartSlug: 'florida-annual-report',
                        teamSlug: teamSlug
                    }
                });
            }
        } catch (error) {
            console.error('Error navigating to annual report cart:', error);
            throw error;
        }
    }

    function reset() {
        currentTeam.value = null;
        subscriptionStatus.value = null;
        paymentMethods.value = [];
        error.value = null;
    }

    return {
        currentTeam,
        subscriptionStatus,
        isTeamLoaded,
        isLoading,
        isAuthenticated,
        error,
        paymentMethods,
        isDefaultPaymentMethodExpiringThisYear,
        defaultPaymentMethod,
        fetchTeamDetails,
        getTeamBySlug,
        getTeamPaymentMethods,
        navigateToAnnualReportCart,
        reset,
        isCardExpiringThisYear,
    };
});
